import { DatePicker, Select } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSteps } from "../../api/courses.api";
import { getSemesters } from "../../api/semesters.api";
import { getEnrolledStudentClasses, getEnrolledStudentLevel, getEnrolledStudentList, getEnrolledStudentSchool, getEnrolledStudentSummary } from "../../api/statistics.api";
import Button from "../../components/Button";
import SimpleHeader from "../../components/layout/SimpleHeader";
import MultiCampusModal from "../../components/statistics/regular-test/modals/MultiCampusModal";
import Classes from "../../components/statistics/student-enrolled/Classes";
import List from "../../components/statistics/student-enrolled/List";
import Summary from "../../components/statistics/student-enrolled/Summary";
import School from "../../components/statistics/student-enrolled/School";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IGetSteps } from "../../models/courses.model";
import { IGetSemesters, ISemester } from "../../models/semesters.model";
import { IEnrolledStudentParams } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import { getReverseYears } from "../../lib/util";
import useLoader from "../../hooks/useLoader";
import EduStepSelect from "../../components/common/EduStepSelect";
import LevelSelect from "../../components/statistics/LevelSelect";
import Level from "../../components/statistics/student-enrolled/Level";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import TableEmpty from "../../components/common/TableEmpty";
import SelectCampusMultiChoice from "../../components/SelectCampusMultiChoice";
import _ from "lodash";

enum Contents {
  Summary,
  Class,
  List,
  School,
  Level
}

const StatisticsStudentEnroll = () => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find(
    (item) => item.commonCode === 'PR0002',
  )?.detailCode;
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSteps>();
  const { arr: res3, isLoading: isLoading3, error: error3, executeArr: execute3 } = useAxios<any>();
  /*
  useLoader(
    isLoading || isLoading2 || isLoading3,
    [error, error2, error3],
  );
  */
  
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [content, setContent] = useState(Contents.Summary);
  const [year, setYear] = useState<string>();
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [levelCode, setLevelCode] = useState<number>();
  const [courseCode, setCourseCode] = useState<number>();
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [date, setDate] = useState(moment().subtract(1, 'days').format('YYYYMMDD'));
  const [pageNo, setPageNo] = useState(1);
  const [multiCampusVisible, setMultiCampusVisible] = useState(false);

  const [clicked, setClicked] = useState<boolean>();
  const [searchClick, setSearchClick] = useState<boolean>();
  const [semesterType, setSemesterType] = useState("RG");
  const [semesterData, setSemesterData] = useState<ISemester[]>([]);
  
  useEffect(() => {
    execute(getSemesters());
    execute2(getSteps());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    setYear(moment().format('YYYY'));
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setLevelCode(defSelValue);
    setCourseCode(defSelValue);
  }

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    getEnrolledStudentData(1);
  }, [content]);
  
  useEffect(() => {
    if (isFirstRun) {
      return;
    }
    getEnrolledStudentData();
  }, [pageNo]);
  
  useEffect(() => {
    setSearchClick(false);
  }, [res3]);

  useEffect(() => {
    if (clicked === undefined && searchClick !== true) {
      return;
    }
    getEnrolledStudentData(pageNo);
  }, [clicked, searchClick]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [year]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [semesterType]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
  }, [res]);

  const handleDataChange = (click: boolean) => {
    setClicked(click);
  }; 
  
  const getPrevReq = (req: IEnrolledStudentParams) => {
    const currentSemester = res?.data.find(item => item.semesterCode === req.semesterCode);
    const prevYear = String(Number(currentSemester?.learningYearCode) - 1);
    const prevSemester = res?.data.find(item => item.learningYearCode === prevYear && item.semesterDiv === currentSemester?.semesterDiv);

    if (prevSemester) {
      return {
        ...req,
        date: moment(req.date).subtract(1, 'years').format('YYYYMMDD'),
        semesterCode: prevSemester.semesterCode
      };
    }

    return null;
  }

  const getEnrolledStudentData = (_pageNo?: number) => {
    if (!semesterCode) {
      setSearchClick(false);
      return infoNotification(t('statistics.studentEnroll.message1'));
    }
    if (!date) {
      setSearchClick(false);
      return infoNotification(t('statistics.studentEnroll.message2'));
    }

    const req: IEnrolledStudentParams = {
      semesterCode,
      date,
      levelCode,
      courseCode,
      campusCode: auth.hqYn === 'Y'
        ? campusCodes
        : (campusCodes.length ? campusCodes : auth.campus),
    }
    if (eduStepCode) {
      req.eduStepCode = eduStepCode;
    }
    const prevReq: any = getPrevReq(req);

    switch (content) {
      case Contents.Summary:
        {
          const arr: any = [getEnrolledStudentSummary(req)];
          if (prevReq) {
            arr.push(getEnrolledStudentSummary(prevReq));
          }
          execute3(arr);
        }
        break;
      case Contents.Class:
        {
          const arr: any = [getEnrolledStudentClasses(req)];
          if (prevReq) {
            arr.push(getEnrolledStudentClasses(prevReq));
          }
          execute3(arr);
        }
        break;
      case Contents.List:
        if(clicked || clicked === undefined) {
          req.perPage = config.pageSize;
          req.pageNo = _pageNo || pageNo;
        } else {
          req.perPage = 99999999;
          req.pageNo = 1;
        }      
        execute3([getEnrolledStudentList(req)]);  
        break;
      case Contents.School:
        execute3([getEnrolledStudentSchool(req)]);
        break;
      case Contents.Level:
        execute3([getEnrolledStudentLevel(req)]);
        break;
    }
  }
  
  const onClickSearch = () => {
    setPageNo(1);
    setSearchClick(true);
  };

  const fnSetSemesterData = (year : string, semesterType : string) => {
    if(res) {
      let data: ISemester[] = _.cloneDeep(res.data);

      if(year) {
        data = data.filter((item: any) => item.learningYearCode === year);
      }

      if(semesterType) {
        data = data.filter((item: any) => item.semesterType === semesterType);
      }

      if(data.length === 0) {
        setSemesterCode(undefined);
      }

      setSemesterData(data);
    }
  }
  
  const renderContent = () => {
    switch (content) {
      case Contents.Summary:
        return (
          <Summary
            res={res3}
          />
        );
      case Contents.Class:
        return (
          <Classes
            res={res3}
          />
        );
      case Contents.List:
        return (
          <List
            // @ts-ignore
            data={res3?.at(0)?.data}
            // @ts-ignore
            total={res3?.at(0)?.total}
            pageNo={pageNo}
            setPageNo={setPageNo}
            semesterCode={semesterCode}
            levelCode={levelCode}
            courseCode={courseCode}
            campusCodes={campusCodes}
            date={date}
            onDataChange={handleDataChange}
            searchClick={searchClick}
          />
        );
      case Contents.School:
        return (
          <School
            res={res3?.at(0)?.data}
            pEduStepCode={eduStepCode}
            searchClick={searchClick}
          />
        )
      case Contents.Level:
        return (
          <Level
            res={res3?.at(0)?.data}
            pEduStepCode={eduStepCode}
            searchClick={searchClick}
          />
        )
    }
  }


  return (
    <>
    <SimpleHeader
      iconFile="icon-statistics-big.svg"
      parentMenu={t('common.menu.statistics')}
      currentMenu={t('statistics.studentEnroll.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
            <div>
              <SelectCampusMultiChoice
                placeholder={t('statistics.eLearning.title2')}
                onChange={setCampusCodes}
                value={campusCodes}
              />
            </div>
            <label>({campusCodes.length ? `${campusCodes.length}${t('statistics.attendance.text1')}` : t('common.text.all')})</label>
          </li>
          <li>
            <label>{t('statistics.refund.placeholder1')}</label> {/* 연도 */}
            <Select
              placeholder={t('statistics.refund.placeholder1')}
              value={year}
              onChange={value => setYear(value)}
            >
              {getReverseYears(SY0005).map(item => (
                <Select.Option
                  key={item.detailCode}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>            
          </li>
          <li>
            <label>
              {t('program.semester.column11')}
            </label>
            <Select
              className="w-120p"
              value={semesterType}
              onChange={value => setSemesterType(value)}
            >
              {
                PR0002?.map(({korName: label, detailCode: value}) => {
                  return (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </li>          
          <li>
            <label>{t('class.class.placeholder4')}</label> {/* 학기 */}
            <Select
              placeholder={t('statistics.regularTest.placeholder2')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
              disabled={!year}
            >
              {semesterData?.map(item => (
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
            <EduStepSelect
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);
                const defAllValue:any = "";
                setLevelCode(defAllValue);
              }}
              width={90}
              includeAll
            />
          </li>
          <li>
            <label>{t('student.student.column26')}</label> {/* 레벨 */}
            <div>
              <LevelSelect
                levelCode={levelCode}
                setLevelCode={value =>{
                  setLevelCode(value);

                  const defSelValue:any = "";
                  setCourseCode(defSelValue);
                }}
                data={res2?.data}
                eduStepCode={eduStepCode}
              />
            </div>
          </li>
          <li>
            <label>{t('student.student.column27')}</label> {/* 단계 */}
            <Select
              placeholder={t('student.student.column27')}
              value={courseCode}
              onChange={value => setCourseCode(value)}
              disabled={!levelCode}
            >
              <Select.Option value="">
                {t('common.text.all')}
              </Select.Option>
              {res2?.data?.find(item => Number(item.courseCode) === levelCode)?.step?.map(item => (
                <Select.Option
                  key={item.courseCode}
                  value={item.courseCode}
                >
                  {item.courseName}
                </Select.Option>
              ))}
            </Select>
          </li>
          <li>
            <label>{t('statistics.studentEnroll.column5')}</label> {/* 기준일 */}
            <div className="date-picker__area">
              <DatePicker
                value={date ? moment(date) : undefined}
                onChange={e => {
                  if (e === null) setDate('');
                  else setDate(moment(e).format('YYYYMMDD'));
                }}
                format={['YYYY-MM-DD', 'YYYYMMDD']}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => onClickSearch()}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading3 && <Loader />}
      <ErrorResponse error={error3} />

      <div className="col-frame">
        {
          res3?.length
            ? (
              <>
                <div className="comm-tab__line">
                  <ul>
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.Summary}
                      tabLabel={t('statistics.attendance.Tab1')}
                    />
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.Class}
                      tabLabel={t('common.menu.class')}
                    />
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.List}
                      tabLabel={t('statistics.homework.tab1')}
                    />
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.School}
                      tabLabel={t('statistics.studentEnroll.tab1')}
                    />
                    <TabItem
                      currentContent={content}
                      onSetContent={setContent}
                      tabContent={Contents.Level}
                      tabLabel={t('statistics.studentEnroll.tab2')}
                    />
                  </ul>
                </div>
                <div>
                  {renderContent()}
                </div>
              </>
              )
            : isLoading3
              ? null
              : (
                <TableEmpty text={t('statistics.appday.text1')} />
                )
        }
      </div>

      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }      
    </div>
    </>
  );
}

export default StatisticsStudentEnroll;